var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
let SimpleButton = class SimpleButton extends LitElement {
    constructor() {
        super(...arguments);
        this.size = 'medium';
        this.label = msg('Button', { id: 'button.default', desc: 'Default button label' });
        this.variant = 'primary';
        this.special = false;
        this.dashed = false;
        this.fluid = false;
        this.disabled = false;
    }
    handleClick(e) {
        this.dispatchEvent(new CustomEvent('button-click', { detail: { label: this.label } }));
        e.preventDefault();
        e.stopPropagation();
    }
    render() {
        const variantClass = `ggo-button--${this.variant}`;
        const sizeClass = `ggo-button--${this.size}`;
        const specialClass = this.special ? 'ggo-button--special' : '';
        const dashedClass = this.dashed ? 'ggo-button--dashed' : '';
        return html `
      <button
        class="ggo-button ${variantClass} ${sizeClass} ${specialClass} ${dashedClass}"
        @click=${this.handleClick}
        ?disabled=${this.disabled}
      >
        <slot></slot>${this.label}
      </button>
    `;
    }
};
SimpleButton.styles = css `
    :host {
      font-family: inherit;
    }

    :host([fluid]) {
      display: block;
      width: 100%;
    }
    :host([fluid]) .ggo-button {
      display: flex;
      width: 100%;
    }

    .ggo-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 8px 16px;
      background: #ffffff;
      border: 1px solid #ccc;
      cursor: pointer;
      transition: background 0.3s, border-color 0.3s, transform 0.2s;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      text-align: center;
      white-space: nowrap;
    }

    /* Inline slot for an optional leading icon (SVG, <img>, …).
       Margin lives on slotted content so an empty slot adds no spacing. */
    ::slotted(*) {
      display: inline-flex;
      align-items: center;
      margin-right: 6px;
    }
    ::slotted(svg) {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }

    .ggo-button:hover {
      background: #f0f0f0;
      border-color: #aaa;
    }

    .ggo-button:active {
      transform: scale(0.95);
    }

    /* Variants */
    .ggo-button--primary {
      background: #0078d4;
      color: #fff;
      border: none;
    }
    .ggo-button--primary:hover {
      background: #005a9e;
    }

    .ggo-button--secondary {
      background: #f0f0f0;
      color: #333;
    }
    .ggo-button--secondary:hover {
      background: #e0e0e0;
    }

    .ggo-button--success {
      background: #28a745;
      color: white;
    }
    .ggo-button--success:hover {
      background: #218838;
    }

    .ggo-button--danger {
      background: #dc3545;
      color: white;
    }
    .ggo-button--danger:hover {
      background: #c82333;
    }

    .ggo-button--warning {
      background: #ffc107;
      color: black;
    }
    .ggo-button--warning:hover {
      background: #e0a800;
    }

    /* Special (gradient) for each variant */
    .ggo-button--primary.ggo-button--special {
      background: linear-gradient(135deg, #0078d4, #00aaff);
    }
    .ggo-button--primary.ggo-button--special:hover {
      background: linear-gradient(135deg, #005a9e, #0088cc);
    }

    .ggo-button--secondary.ggo-button--special {
      background: linear-gradient(135deg, #cccccc, #f0f0f0);
    }
    .ggo-button--secondary.ggo-button--special:hover {
      background: linear-gradient(135deg, #aaaaaa, #e0e0e0);
    }

    .ggo-button--success.ggo-button--special {
      background: linear-gradient(135deg, #28a745, #5cd65c);
    }
    .ggo-button--success.ggo-button--special:hover {
      background: linear-gradient(135deg, #1e7e34, #4caf50);
    }

    .ggo-button--danger.ggo-button--special {
      background: linear-gradient(135deg, #dc3545, #ff6f71);
    }
    .ggo-button--danger.ggo-button--special:hover {
      background: linear-gradient(135deg, #b52a37, #e63946);
    }

    .ggo-button--warning.ggo-button--special {
      background: linear-gradient(135deg, #ffc107, #ffe066);
    }
    .ggo-button--warning.ggo-button--special:hover {
      background: linear-gradient(135deg, #e0a800, #ffd54f);
    }

    /* Dashed (outlined) variant — transparent fill, dashed border */
    .ggo-button--primary.ggo-button--dashed {
      background: transparent;
      color: #0078d4;
      border: 2px dashed #0078d4;
    }
    .ggo-button--primary.ggo-button--dashed:hover {
      background: rgba(0, 120, 212, 0.08);
      border-color: #005a9e;
      color: #005a9e;
    }

    .ggo-button--secondary.ggo-button--dashed {
      background: transparent;
      color: #6c757d;
      border: 2px dashed #6c757d;
    }
    .ggo-button--secondary.ggo-button--dashed:hover {
      background: rgba(108, 117, 125, 0.08);
      border-color: #4d5358;
      color: #4d5358;
    }

    .ggo-button--success.ggo-button--dashed {
      background: transparent;
      color: #28a745;
      border: 2px dashed #28a745;
    }
    .ggo-button--success.ggo-button--dashed:hover {
      background: rgba(40, 167, 69, 0.08);
      border-color: #218838;
      color: #218838;
    }

    .ggo-button--danger.ggo-button--dashed {
      background: transparent;
      color: #dc3545;
      border: 2px dashed #dc3545;
    }
    .ggo-button--danger.ggo-button--dashed:hover {
      background: rgba(220, 53, 69, 0.08);
      border-color: #b52a37;
      color: #b52a37;
    }

    .ggo-button--warning.ggo-button--dashed {
      background: transparent;
      color: #b78400;
      border: 2px dashed #ffc107;
    }
    .ggo-button--warning.ggo-button--dashed:hover {
      background: rgba(255, 193, 7, 0.12);
      border-color: #e0a800;
      color: #8a6500;
    }

    /* Sizes */
    .ggo-button--small {
      padding: 7px 14px;
      font-size: 13px;
      font-weight: 500;
      border-radius: 6px;
    }

    .ggo-button--medium {
      padding: 8px 16px;
      font-size: 14px;
    }

    .ggo-button--large {
      height: 47px; /* Fixe la hauteur à 47px */
      padding: 0 20px; /* Ajuste le padding pour centrer le contenu */
      font-size: 20px;
    }

    /* Disabled state */
    .ggo-button:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background: #e0e0e0; /* fallback background for disabled */
      color: #888;          /* fallback text color */
      border-color: #ccc;
    }

    /* If you want to override hover effects for disabled buttons */
    .ggo-button:disabled:hover {
      background: #e0e0e0;
      border-color: #ccc;
  }
  `;
__decorate([
    property({ type: String })
], SimpleButton.prototype, "size", void 0);
__decorate([
    property({ type: String })
], SimpleButton.prototype, "label", void 0);
__decorate([
    property({ type: String })
], SimpleButton.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean })
], SimpleButton.prototype, "special", void 0);
__decorate([
    property({ type: Boolean })
], SimpleButton.prototype, "dashed", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SimpleButton.prototype, "fluid", void 0);
__decorate([
    property({ type: Boolean })
], SimpleButton.prototype, "disabled", void 0);
SimpleButton = __decorate([
    localized(),
    customElement('simple-button')
], SimpleButton);
export { SimpleButton };
//# sourceMappingURL=SimpleButton.js.map